import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { hexToHsl } from "@/context/SettingsContext";

/**
 * The platform's look (Platform console → Settings → Appearance): app name, icon,
 * colour and the sign-in page promo. Loaded once from /api/meta and shared.
 */
const ROUND_FAVICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='9' fill='%2343295F'/%3E%3Ccircle cx='16' cy='16' r='9' fill='none' stroke='white' stroke-width='2.4'/%3E%3Ccircle cx='16' cy='16' r='2.4' fill='white'/%3E%3C/svg%3E";

// The last look seen on this device, so a refresh paints it at once instead of
// flashing the standard sign-in page first.
const STORE = "mt_meta";
function readStored() {
  try {
    const raw = window.localStorage.getItem(STORE);
    return raw ? { ...JSON.parse(raw), loaded: true, stale: true } : null;
  } catch {
    return null;
  }
}
let cached = readStored();
let pending = null;
const listeners = new Set();

export function loadMeta(force = false) {
  if (!force && cached && !cached.stale) return Promise.resolve(cached);
  if (!force && pending) return pending;
  pending = api
    .get("/meta")
    .then(({ data }) => {
      cached = { ...data, loaded: true };
      try {
        const { needs_setup, ...keep } = data; // eslint-disable-line no-unused-vars
        window.localStorage.setItem(STORE, JSON.stringify(keep));
      } catch {
        /* private browsing */
      }
      applyHead(cached);
      listeners.forEach((fn) => fn(cached));
      return cached;
    })
    .catch(() => {
      cached = cached || { platform_name: "MetaTrack", needs_setup: false, loaded: true };
      listeners.forEach((fn) => fn(cached));
      return cached;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** The current look; re-renders when it's saved from the Appearance page. */
export function useMeta() {
  const [meta, setMeta] = useState(cached || { platform_name: "MetaTrack", needs_setup: false, loaded: false });
  useEffect(() => {
    listeners.add(setMeta);
    loadMeta();
    return () => listeners.delete(setMeta);
  }, []);
  return meta;
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setMetaTag(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

/** Tab icon, home-screen icon and the phone's top-bar colour follow the saved look. */
export function applyHead(meta) {
  if (!meta) return;
  const v = meta.icon_v || (meta.brand_color || "").replace("#", "");
  // the round purple tab icon stays until a custom icon is uploaded in Platform settings
  setLink("icon", meta.icon_v ? `/api/brand/icon-32.png?v=${v}` : ROUND_FAVICON);
  setLink("apple-touch-icon", `/api/brand/icon-180.png?v=${v}`);
  if (meta.brand_color) setMetaTag("theme-color", meta.brand_color);
  if (meta.platform_name) setMetaTag("apple-mobile-web-app-title", meta.platform_name);
}

/** CSS variables that make every brand-* class use this colour inside one element (e.g. the sign-in page). */
export function brandVars(hex) {
  const { h, s, l } = hexToHsl(hex || "#43295F");
  const base = Math.min(l, 45);
  return {
    "--brand": `${h} ${s}% ${base}%`,
    "--brand-hover": `${h} ${s}% ${Math.max(base - 8, 12)}%`,
    "--brand-light": `${h} ${Math.min(s, 40)}% 94%`,
    "--brand-ring": `${h} ${Math.min(s, 40)}% 85%`,
    "--primary": `${h} ${s}% ${base}%`,
    "--ring": `${h} ${s}% ${base}%`,
  };
}

if (cached) applyHead(cached);

/**
 * The sign-in promo image, kept on the device so a refresh shows it instantly instead of
 * waiting to download it again. Replaced automatically when a new image is saved.
 */
const PROMO_KEY = "mt_promo";
export function promoSrc(meta) {
  if (!meta?.promo_url) return "";
  try {
    const kept = JSON.parse(window.localStorage.getItem(PROMO_KEY) || "null");
    if (kept && kept.v === meta.promo_v && kept.data) return kept.data;
  } catch {
    /* fall back to the address */
  }
  return meta.promo_url;
}
export async function keepPromo(meta) {
  if (!meta?.promo_url || !meta.promo_v) return;
  try {
    const kept = JSON.parse(window.localStorage.getItem(PROMO_KEY) || "null");
    if (kept && kept.v === meta.promo_v) return;
    const blob = await (await fetch(meta.promo_url)).blob();
    const data = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    window.localStorage.setItem(PROMO_KEY, JSON.stringify({ v: meta.promo_v, data }));
  } catch {
    /* too big for this browser's storage — it still loads from the server */
  }
}
